import {
  IconBuildingWarehouse,
  IconCoin,
  IconPackage,
  IconPhoneOff,
  IconReportAnalytics,
  IconUsers,
  IconCashBanknote,
  IconClipboardList,
  IconHome,
  IconSettings,
  IconSpeakerphone,
  IconToolsKitchen3,
  IconTruckDelivery,
} from "@tabler/icons-react";
import type { ReactNode } from "react";
import type { Module } from "../lib/api";
import { STREAM, type StreamKey } from "../theme/mantine-theme";

/**
 * Every module, once: its label, its landing route, its icon and its blurb.
 *
 * The blurbs were on the Home screen and the labels were about to be repeated
 * in the bottom nav and the desktop rail, which is three copies of the same
 * seven strings. They live here instead, so the nav and the dashboard cannot
 * disagree about what a module is called.
 *
 * What is NOT here is which modules to show. That is the session's business -
 * see moduleNav below - because a nav that hard-coded the list would offer a
 * Payment Collection Agent a Service tab that refuses them.
 */
export interface ModuleMeta {
  label: string;
  /** For the nav, where 58px of width has to hold it. */
  shortLabel: string;
  blurb: string;
  path: string;
  icon: ReactNode;
  /**
   * The module's stream token in the theme, for Home's accents and the nav.
   *
   * A token rather than a colour, so a module cannot introduce a colour the
   * palette has never heard of - the same rule StateBadge follows.
   */
  stream: StreamKey;
}

const ICON_PROPS = { size: 21, stroke: 1.6 } as const;

export const MODULE_META: Record<Module, ModuleMeta> = {
  MARKETING: {
    label: "Marketing",
    shortLabel: "Marketing",
    blurb: "Place day orders out in the field",
    path: "/marketing",
    stream: "marketing",
    icon: <IconSpeakerphone {...ICON_PROPS} />,
  },
  COLLECTIONS: {
    label: "Collections",
    shortLabel: "Collect",
    blurb: "Record payments against issued bills",
    path: "/collections",
    stream: "collections",
    icon: <IconCashBanknote {...ICON_PROPS} />,
  },
  SALES: {
    label: "Sales",
    shortLabel: "Sales",
    blurb: "Review day orders, create orders, generate bills",
    path: "/sales",
    stream: "sales",
    icon: <IconClipboardList {...ICON_PROPS} />,
  },
  SERVICE: {
    label: "Service",
    shortLabel: "Service",
    blurb: "Collection, workshop intake, diagnosis and outcomes",
    path: "/service",
    stream: "service",
    icon: <IconToolsKitchen3 {...ICON_PROPS} />,
  },
  DELIVERY: {
    label: "Delivery",
    shortLabel: "Delivery",
    blurb: "New sales and serviced-battery returns",
    path: "/delivery",
    stream: "delivery",
    icon: <IconTruckDelivery {...ICON_PROPS} />,
  },
  INVENTORY: {
    label: "Inventory",
    shortLabel: "Stock",
    blurb: "Stock in, stock levels, movement ledger",
    path: "/inventory",
    stream: "inventory",
    icon: <IconBuildingWarehouse {...ICON_PROPS} />,
  },
  ADMIN: {
    label: "Admin",
    shortLabel: "Admin",
    blurb: "Users, roles, products and full data access",
    path: "/admin",
    stream: "admin",
    icon: <IconSettings {...ICON_PROPS} />,
  },
};

/** Home is not a module - nobody is granted it - but it is a nav entry. */
export const HOME_ENTRY = {
  key: "HOME",
  label: "Home",
  shortLabel: "Home",
  path: "/",
  icon: <IconHome {...ICON_PROPS} />,
};

/**
 * A module's accent colour: the 3px left border on its work rows, and the
 * tint behind its card icon.
 *
 * Read through MODULE_META so the module -> token mapping lives in exactly
 * one place, and returned as plain CSS colours because these are borders and
 * backgrounds rather than Mantine component props.
 */
export function moduleAccent(module: Module): { fg: string; bg: string } {
  // No fallback and no narrowing needed: every STREAM token carries both
  // tints by construction, which is the reason the streams are their own map.
  const token = STREAM[MODULE_META[module].stream];
  return { fg: token.fg, bg: token.bg };
}

// ---------------------------------------------------------------------------
// The navigation, in groups
// ---------------------------------------------------------------------------

/**
 * One entry in the nav. Either a MODULE - gated on what the session holds -
 * or a plain link gated on the roles that own the master lists.
 */
export interface NavLink {
  key: string;
  label: string;
  path: string;
  icon: ReactNode;
  /** Set when this entry is a module, so the badge can be looked up. */
  module?: Module;
}

export interface NavGroup {
  key: string;
  /** Drawn above the group, and read out to a screen reader as its name. */
  label: string;
  items: NavLink[];
}

const LINK_ICON = { size: 19, stroke: 1.6 } as const;

/**
 * Which modules are field work, and which are the desk's.
 *
 * Stated by the business rather than derived: "field staff" is not a property
 * of a module that code could work out, and an ordering invented here would
 * drift from how the shop actually talks about itself the first time somebody
 * disagreed with it.
 *
 * Collections sits with the field modules because a Payment Collection Agent
 * collects at the customer's premises - the same day, the same van, the same
 * phone as a delivery.
 */
const FIELD_MODULES: readonly Module[] = [
  "SERVICE",
  "SALES",
  "MARKETING",
  "DELIVERY",
  "COLLECTIONS",
];

/** Inventory is the desk's: stock levels, receipts and the ledger. */
const STORE_MODULES: readonly Module[] = ["INVENTORY"];

/**
 * The master lists, which are not modules at all.
 *
 * They were only ever reachable from Home, which made Home the app's menu as
 * well as its dashboard. They belong in the nav beside the module that reads
 * from them.
 */
const STORE_LINKS: NavLink[] = [
  { key: "products", label: "Products", path: "/products", icon: <IconPackage {...LINK_ICON} /> },
  { key: "customers", label: "Customers", path: "/customers", icon: <IconUsers {...LINK_ICON} /> },
  { key: "prices", label: "Price list", path: "/prices", icon: <IconCoin {...LINK_ICON} /> },
  {
    key: "missing-phone",
    label: "Missing phones",
    path: "/customers/missing-phone",
    icon: <IconPhoneOff {...LINK_ICON} />,
  },
  {
    key: "reports",
    label: "Reports",
    path: "/reports",
    icon: <IconReportAnalytics {...LINK_ICON} />,
  },
];

/**
 * The nav, grouped, for whoever is signed in.
 *
 * Empty groups are dropped rather than drawn with a heading and nothing under
 * it: a Payment Collection Agent should not be shown that "Store management"
 * exists and holds nothing for them.
 */
export function navGroups(
  granted: readonly Module[],
  canManageStore: boolean,
): NavGroup[] {
  const held = (list: readonly Module[]) =>
    list
      .filter((module) => granted.includes(module))
      .map((module) => ({
        key: module,
        label: MODULE_META[module].label,
        path: MODULE_META[module].path,
        icon: MODULE_META[module].icon,
        module,
      }));

  const groups: NavGroup[] = [
    { key: "field", label: "Field staff", items: held(FIELD_MODULES) },
    {
      key: "store",
      label: "Store management",
      items: [...held(STORE_MODULES), ...(canManageStore ? STORE_LINKS : [])],
    },
    {
      key: "admin",
      label: "Admin",
      items: granted.includes("ADMIN")
        ? [
            {
              key: "ADMIN",
              label: "User management",
              path: MODULE_META.ADMIN.path,
              icon: MODULE_META.ADMIN.icon,
              module: "ADMIN" as Module,
            },
          ]
        : [],
    },
  ];

  return groups.filter((group) => group.items.length > 0);
}

/**
 * Which nav entry a path belongs to, across groups and plain links.
 *
 * Longest prefix wins, as before - without it "/customers" and
 * "/customers/missing-phone" would both claim the same row.
 */
export function activeNavEntry(pathname: string, groups: NavGroup[]): string {
  const candidates = groups
    .flatMap((group) => group.items)
    .sort((a, b) => b.path.length - a.path.length);
  const hit = candidates.find(
    (candidate) =>
      pathname === candidate.path || pathname.startsWith(`${candidate.path}/`),
  );
  return hit?.key ?? (pathname === "/" ? HOME_ENTRY.key : "");
}
